import React, { Fragment, useState, useEffect, useCallback } from "react";
import Label from "../../components/label";
import PropTypes from "prop-types";
import CustomModal from "../../components/modal";
import { getFormattedTimeWithTimeZoneCapital, getMomentObjFormatterWithTimeZone } from "../../utils/dateTimeHelper";
import { getUserProfile } from "../../apiRoutes";
import AddShiftForm from "./AddShiftForm";
import InstaButton from "../../components/button";
import moment from "moment";
import ICCheckbox from "../../components/form/checkbox";
import { useFormik } from "formik";
import ICSelect from "../../components/form/selectInput";
import MakeAPICall from "../../apiCalls/common";

const AssignedShiftLabels = ({ data, scheduleFilter, handleShiftLabelClick, currentDoctorId, timezone }) => {
  return (data.map((shift) => {
    return (<>
      {getShiftColors(shift, scheduleFilter)
        .map(color => renderShiftWithColor(shift, handleShiftLabelClick, color, currentDoctorId, timezone))}
    </>);
  }));
};

function renderShiftWithColor(shift, handleShiftLabelClick, color, currentDoctorId, timezone) {
  const disabled = moment(shift.shiftDate, "DD/MM/YYYY").isBefore(moment());
  const isToday = moment(shift.shiftDate, "DD/MM/YYYY").isSame(moment(), 'day');
  const isCurrentDoctor = currentDoctorId === shift.doctorId._id;
  return (<div key={shift._id + color} className={`assigned-shifts-labels-container mt-2 ${(!isCurrentDoctor && !isToday) ? 'another-doc-shift-lbl' : ''}`} data-toggle="tooltip"
               data-placement="top" style={{
    backgroundColor: color,
    opacity:  disabled ? 0.5 : 1,
    cursor: disabled ? "not-allowed" : "pointer"
  }}
               aria-disabled={disabled}
               onClick={(e) => handleShiftLabelClick(e, shift)}>
          <span className={"badge " + color}>
            <p className="shift-person">{getShiftName(color)}</p> <br />
            <p className="shift-person">
            {shift.doctorId?.firstName} {shift.doctorId?.lastName}
            </p>
            <br />
             <p
        className="shift-end-time">{getFormattedTimeWithTimeZoneCapital(shift.shiftStartTime, timezone)} to {getFormattedTimeWithTimeZoneCapital(shift.shiftEndTime, timezone)}</p>
          </span>
  </div>);
}

function getShiftName(color) {
  if (color === "bg-instant-switch") return "Instant Switch";
  if (color === "bg-instant-leaf") return "Instant Leaf";
  if (color === "bg-instant-hush") return "Instant Hush";

  return "Shift";
}

function getShiftColors(shift, scheduleFilter) {
  const colors = [];
  const allowAll = !scheduleFilter || !scheduleFilter.length;

  if (shift.shiftFor === "INSTANT_SWITCH" && (allowAll || scheduleFilter?.some(filter => filter.value === "Smoking Cessation"))) colors.push("bg-instant-switch");
  if (shift.shiftFor === "INSTANT_LEAF" && (allowAll || scheduleFilter?.some(filter => filter.value === "Medicinal Cannabis"))) colors.push("bg-instant-leaf");
  if (shift.shiftFor === "INSTANT_HUSH" && (allowAll || scheduleFilter?.some(filter => filter.value === "Instant Hush"))) colors.push("bg-instant-hush");

  return colors;
}

const CalendarUI = props => {
  const DAY_OF_WEEKS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const [selectedDay, setSelectedDay] = useState(null);
  const [dataToEdit, setDataToEdit] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isOpenAddShiftModal, setIsOpenAddShiftModal] = useState(false);
  const [currentTimezone, setCurrentTimezone] = useState(props.timeZone);
  const [shiftsByDate, setShiftsByDate] = useState(props.shiftsByDate)
  const formik = useFormik({
    initialValues: {
      timezone: userData ? userData : ''
    }
  });
  useEffect(() => {
    if (userData) {
      formik.setValues({ timezone: userData });
      setCurrentTimezone(userData)
    }
    setUserData(props.timeZone);
  }, [props.timeZone, userData]);
  const timeZoneList = [
    {
      val: "Australia/Sydney", _id: "Australia/Sydney"
    },
    {
      val: "Australia/Melbourne", _id: "Australia/Melbourne"
    },
    {
      val: "Australia/Brisbane", _id: "Australia/Brisbane"
    },
    {
      val: "Australia/Perth", _id: "Australia/Perth"
    },
    {
      val: "Australia/Adelaide", _id: "Australia/Adelaide"
    },
    {
      val: "Australia/Darwin", _id: "Australia/Darwin"
    }
  ]
  const changeTimezone = (value) => {
    setCurrentTimezone(value)
  };
  const openDoctorSchedulePopup = (data) => {
    setDataToEdit(null);
    const date = getMomentObjFormatterWithTimeZone(data.date, "DD/MM/YYYY");

    if (date.isAfter(moment())) {
      setSelectedDay(date);
      setIsOpenAddShiftModal(true);
    }
  };

  const closeAddShiftModal = () => {
    setIsOpenAddShiftModal(false);
    setSelectedDay(null);
  };

  useEffect(() => {
    Object.keys(shiftsByDate).forEach(date => {
      shiftsByDate[date].forEach(shift => {
        const shiftStartTimeUTC = new Date(shift.shiftStartTime);
        const shiftEndTimeUTC = new Date(shift.shiftEndTime);

        // Use the Intl.DateTimeFormat API for accurate time zone conversion
        const options = { timeZone: currentTimezone, hour12: false, year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };

        shift.shiftStartTimeLocal = new Intl.DateTimeFormat('en-US', options).format(shiftStartTimeUTC);
        shift.shiftEndTimeLocal = new Intl.DateTimeFormat('en-US', options).format(shiftEndTimeUTC);
      });
    });
    setShiftsByDate(props.shiftsByDate)
  }, [changeTimezone]);

  const handleShiftLabelClick = (e, data) => {
    e.preventDefault();
    e.stopPropagation();
    const date = getMomentObjFormatterWithTimeZone(data.shiftDate, "DD/MM/YYYY");

    const doctor = props.doctorList.at(0);
    if (date.isAfter(moment()) && data.doctorId._id === doctor._id) {
      setDataToEdit({
        ...data,
        doctorId: data.doctorId._id,
        doctorName: `${data.doctorId.firstName} ${data.doctorId.lastName}`,
      });

      setSelectedDay(date);
      setIsOpenAddShiftModal(true);
    }
  };

  return (<>
    <div className="row p-0 m-0 month-buttons mt-4 w-100">
      <div className="text-left row w-100 ml-1 mr-4">
        <InstaButton title="Previous" clickEvent={() => props.getPreviousMonthCalender()} />
        <InstaButton className="ml-2" title="Next" clickEvent={() => props.getNextMonthCalender()} />
        <div className="flex-fill"></div>
        <Label title={"My Timezone "} className={"mr-2 mt-2 mb-0"} />
        <ICSelect
          label="" className={"w-50"}
          id="timezone"
          name="timezone"
          placeholder=""
          useId="_id"
          render={val => `${val._id}`}
          isCombined={true}
          items={timeZoneList}
          onChange={val => setCurrentTimezone(val)}
          handleChange={val => {
            formik.handleChange("timezone");
            setCurrentTimezone(val);
            formik.setFieldValue("timezone", val);
          }}
          onBlur={formik.handleBlur}
          value={formik.values.timezone}
        />
        {/* <Label title={" " + new Date().toLocaleTimeString()} className={"mr-2 mt-2 mb-0"} /> */}
        {/* <InstaButton className="insta-button-primary small border-radius-0 m-0" title="Add Shift"
          clickEvent={() => openDoctorSchedulePopup({
            date: moment().add(1, "d").format('DD/MM/YYYY')
          })}
        /> */}
        <div className="mr-4 d-flex flex-row align-items-center">
          <Label className="m-0 p-0 ml-2" title="All Doctors Scheduled" />
          <div className="ic-checkbox-container ml-2">
          <ICCheckbox
            name="myPostsCheckbox"
            onChange={props.applyDoctorsScheduledFilter}
            value={props.isAllDoctorScheduled}
          />
          </div>
        </div>
        

        <InstaButton className="insta-button-primary small border-radius-0 m-0" title="Add Shift"
                     clickEvent={() => openDoctorSchedulePopup({
                       date: moment().add(1, "d").format('DD/MM/YYYY')
                     })}
        />
      </div>
    </div>

    <div className="row p-0 m-0 calendar-container mt-4">
      {DAY_OF_WEEKS.map((day, index) => {
        return (<div key={index} className="calendar-day-of-week-column">
          <Label title={day} className="w-100 text-center secondary-font m-0" />
        </div>);
      })}
      {props.calendarDates.map((week, index) => {
        return week.map((day, dayIndex) => {
          return (<div key={`${index}-${dayIndex}`} className="calendar-column cursor-pointer"
                       onClick={() => openDoctorSchedulePopup(day)}>
            <Label title={day.date} className="w-100 text-center" />
            {shiftsByDate[day.date] &&
              <AssignedShiftLabels data={shiftsByDate[day.date]} scheduleFilter={props.scheduleFilter}
                handleShiftLabelClick={(e, data) => handleShiftLabelClick(e, data)} currentDoctorId={props.doctorList.length ? props.doctorList.at(0)._id : ''} timezone={currentTimezone}/>}
          </div>);
        });
      })}
    </div>
    <CustomModal
      openModal={isOpenAddShiftModal}
      title={dataToEdit ? "Edit Shift" : "Add Shift"}
      noButtons={true}
      size="lg"
      cancelEvent={() => closeAddShiftModal()}
      child={<AddShiftForm
        onFormSubmit={(formData) => {
          closeAddShiftModal();
          props.handleShiftFormSubmit(formData, !!dataToEdit);
        }}
        dataToEdit={dataToEdit}
        selectedDay={selectedDay}
        doctorList={props.doctorList}
        timeZone={currentTimezone}
        deleteShift={() => {
          closeAddShiftModal();
          props.deleteShift(dataToEdit._id);
        }}
      />}
    />
  </>);
};

export default CalendarUI;

CalendarUI.defaultProps = {
  calendarDates: []
};

CalendarUI.propTypes = {
  calendarDates: PropTypes.array
};